import axios from 'axios';
import { getDecodedToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URI+'/api' || 'http://localhost:3002/api';

// Get comments without requiring authentication
export const getComments = async (parentContent, contentType, page = 1, limit = 10) => {
  try {
    // Check for required parameters
    if (!parentContent || !contentType) {
      throw new Error('Parent content ID and content type are required');
    }
    
    const response = await axios.get(`${API_URL}/comments/content`, {
      params: {
        parentContent,
        contentType,
        page,
        limit
      }
    });
    console.log('getComments raw response:', response);
    
    // Extract comments
    let comments = [];
    let pagination = {
      page,
      limit,
      total: 0,
      pages: 1
    };
    
    // Handle different possible response structures
    if (response.data) {
      // Check for standard API format
      if (response.data.data && response.data.data.comments) {
        comments = response.data.data.comments;
        
        if (response.data.pagination) {
          pagination = response.data.pagination;
        }
      }
      // Check for alternate format where comments are directly in data
      else if (response.data.comments) {
        comments = response.data.comments;
        
        if (response.data.pagination) {
          pagination = response.data.pagination;
        }
      }
      // Check if data itself is the comments array
      else if (Array.isArray(response.data)) {
        comments = response.data;
      }
      // Check for data.data structure without comments property
      else if (response.data.data && Array.isArray(response.data.data)) {
        comments = response.data.data;
      }
    }
    
    // Ensure pagination calculations
    if (pagination.total && !pagination.pages) {
      pagination.pages = Math.ceil(pagination.total / limit);
    }

    console.log('Extracted comments:', comments);
    console.log('Pagination:', pagination);
    
    // Return consistently structured response
    return {
      data: {
        comments: Array.isArray(comments) ? comments : [],
        pagination
      }
    };
  } catch (error) {
    console.error('Error in getComments:', error);
    throw new Error(error.response?.data?.message || 'Failed to fetch comments');
  }
};

// Create comment (requires authentication)
export const createComment = async (commentData) => {
  try {
    // Check if this is a guest comment
    const isGuestComment = commentData.isGuestComment && commentData.guestAuthor;

    // For authenticated users, validate token
    if (!isGuestComment) {
      const token = localStorage.getItem('token');
      console.log('createComment token:', token);
      if (!token) {
        throw new Error('Please login to post a comment');
      }

      // Validate decoded token
      const decodedToken = getDecodedToken();
      console.log('createComment decodedToken:', decodedToken);
      if (!decodedToken) {
        throw new Error('Invalid authentication token. Please login again');
      }
      
      const userId = decodedToken.id || decodedToken._id;
      if (!userId) {
        throw new Error('User ID not found in token. Please login again');
      }
      
      // For authenticated users, set the author ID
      commentData.author = userId;
    }

    // Validate required fields
    if (!commentData.content) {
      throw new Error('Comment content is required');
    }
    if (!commentData.parentContent) {
      throw new Error('Parent content ID is required');
    }
    if (!commentData.contentType) {
      throw new Error('Content type is required');
    } 
    
    // Validate guest author info if needed
    if (isGuestComment) {
      const { firstName, lastName, email } = commentData.guestAuthor;
      if (!firstName || !lastName || !email) {
        throw new Error('Guest author information is incomplete');
      }
    }

    // Set headers based on auth status
    const headers = isGuestComment ? 
      {} : 
      { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const response = await axios.post(
      `${API_URL}/comments`, 
      commentData,
      {
        headers
      }
    );

    if (!response.data) {
      throw new Error('No response received from server');
    }

    return response.data;
  } catch (error) {
    console.error('Error in createComment:', error);
    
    // Handle specific axios errors
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const serverMessage = error.response.data?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      }
      switch (error.response.status) {
        case 401:
          throw new Error('Authentication failed. Please login again');
        case 403:
          throw new Error('You do not have permission to post comments');
        case 404:
          throw new Error('The comment could not be created. Resource not found');
        case 422:
          throw new Error('Invalid comment data. Please check your input');
        default:
          throw new Error(`Server error (${error.response.status}). Please try again later`);
      }
    } else if (error.request) {
      // The request was made but no response was received
      throw new Error('No response from server. Please check your internet connection');
    }
    
    // If it's our own error with a message, throw that
    if (error.message) {
      throw error;
    }

    // Generic error
    throw new Error('Failed to create comment. Please try again');
  }
};

// Toggle comment like (requires authentication)
export const toggleCommentLike = async (commentId) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await axios.post(
      `${API_URL}/comments/${commentId}/like`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to toggle like');
  }
};

// Delete comment (requires authentication)
export const deleteComment = async (commentId, isAdmin = false) => {
  try {
    // Check if this is a guest comment that belongs to the current user
    const guestCommentIds = localStorage.getItem('guestCommentIds');
    const parsedIds = guestCommentIds ? JSON.parse(guestCommentIds) : [];
    const isGuestComment = parsedIds.includes(commentId);
    
    // Get authentication token (required for regular user or admin)
    const token = localStorage.getItem('token');
    
    // For admin deletion, add admin flag to the URL
    if (isAdmin && token) {
      console.log('### comment Id', commentId);
      const response = await axios.delete(`${API_URL}/comments/${commentId}?adminDelete=true`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    }
    // For authenticated user comments, require token
    else if (!isGuestComment) {
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const response = await axios.delete(`${API_URL}/comments/${commentId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } else {
      // For guest comments, we'll use a different approach
      // Here we include a special parameter to indicate it's a guest comment deletion
      const response = await axios.delete(`${API_URL}/comments/${commentId}?guestComment=true`);
      return response.data;
    }
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to delete comment');
  }
};

// Update a comment
export const updateComment = async (commentId, commentData) => {
  try {
    const response = await axios.put(
      `${API_URL}/comments/${commentId}`,
      commentData,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating comment:', error);
    throw error.response?.data || error;
  }
};

// Get a single comment with its replies
export const getCommentWithReplies = async (commentId) => {
  try {
    const response = await axios.get(
      `${API_URL}/comments/${commentId}`,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching comment:', error);
    throw error.response?.data || error;
  }
}; 